import { useState, useEffect, useRef, useCallback } from "react";
import CreateTeamForm from "./CreateTeamForm";
import ViewTeams from "./ViewTeams";
import ViewGameMessages from "./ViewGameMessages";
import ViewGameActions from "./ViewGameActions";
import TurnLengthModal from "./TurnLengthModal";
import TurnTimer from "./TurnTimer";
import ExtendTimeModal from "./ExtendTimeModal";
import ExtendGameModal from "./ExtendGameModal";
import { useTimer } from "../contexts/TimerContext";
import { GAME_STATUSES } from "../shared/constants";
import { authenticatedFetch } from "../utils/api";
import type { AdminGame as Game, AdminTeam as Team, AdminPlayer as Player, AdminMessage as Message, AdminAction as Action } from "../types/admin";

interface GameCardProps {
  game: Game;
  onUpdate?: (updatedGame: Game) => void;
  teams?: Team[];
  players?: Player[];
  playersByTeam?: Record<string, Player[]>;
  messages?: Message[];
  actions?: Action[];
  playerActionCounts?: Record<string, number>;
  playerCharCounts?: Record<string, number>;
  playerLandingSiteInvestments?: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>;
  hasUnreadActions?: boolean;
  onClearActionsNotification?: () => void;
  onViewingActionsChange?: (isViewing: boolean) => void;
}

export default function GameCard({ game, onUpdate, teams, playersByTeam, messages, actions, playerActionCounts, playerCharCounts, playerLandingSiteInvestments, hasUnreadActions = false, onClearActionsNotification, onViewingActionsChange }: GameCardProps) {
  const [currentGame, setCurrentGame] = useState<Game>(game);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showCreateTeamForm, setShowCreateTeamForm] = useState<boolean>(false);
  const [showViewTeams, setShowViewTeams] = useState<boolean>(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);
  const [showViewMessages, setShowViewMessages] = useState<boolean>(false);
  const [showViewActions, setShowViewActions] = useState<boolean>(false);
  const [showTurnLengthModal, setShowTurnLengthModal] = useState<boolean>(false);
  const [pendingTurnAction, setPendingTurnAction] = useState<"inc" | "dec" | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousGameIdRef = useRef<string | null>(null);
  const hasAutoOpenedRef = useRef<boolean>(false);
  const { startTimer, timerSeconds, isPaused, pauseTimer, resumeTimer } = useTimer();
  const [showExtendTimeModal, setShowExtendTimeModal] = useState<boolean>(false);
  const [showExtendGameModal, setShowExtendGameModal] = useState<boolean>(false);
  const displayTimerSeconds = timerSeconds ?? 0;

  const closeAllSections = useCallback(() => {
    setShowCreateTeamForm(false);
    setShowViewTeams(false);
    setShowViewMessages(false);
    setShowViewActions(false);
    setShowStatusDropdown(false);
  }, []);

  // Update local state when game prop changes
  useEffect(() => {
    const isNewGame = previousGameIdRef.current !== game.game_id;
    previousGameIdRef.current = game.game_id;
    setCurrentGame(game);
    
    // Automatically open Teams view when a new game is loaded (simulate clicking the button)
    if (isNewGame) {
      hasAutoOpenedRef.current = false;
      // Simulate clicking "Teams" button: close all sections first, then open Teams view
      closeAllSections();
      // Use setTimeout to ensure state updates happen in the right order
      setTimeout(() => {
        setShowViewTeams(true);
        hasAutoOpenedRef.current = true;
      }, 0);
    }
  }, [game, closeAllSections]);

  // Notify parent when viewing actions state changes
  useEffect(() => {
    if (onViewingActionsChange) {
      onViewingActionsChange(showViewActions);
    }
  }, [showViewActions, onViewingActionsChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Teams are already filtered by game_id in AdminActions state, no need to filter again
  const teamsForCurrentGame = teams || [];

  const handleIncrementTurn = () => {
    setPendingTurnAction("inc");
    setShowTurnLengthModal(true);
  };

  const handleTurnLengthSubmit = (totalSeconds: number) => {
    startTimer(totalSeconds);
    
    const action = pendingTurnAction;
    setPendingTurnAction(null);
    setShowTurnLengthModal(false);
    
    if (!action) return;
    
    setIsLoading(true);
    setError("");

    // First, broadcast timer start to all players via backend
    authenticatedFetch(`/games/timer/start?game_id=${currentGame.game_id}&timer_seconds=${totalSeconds}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    }).catch(err => {
      console.error("Failed to broadcast timer:", err);
      // Continue even if timer broadcast fails
    });

    const endpoint = action === "inc" 
      ? `/games/turn/inc?game_id=${currentGame.game_id}`
      : `/games/turn/dec?game_id=${currentGame.game_id}`;

    authenticatedFetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.detail || `Failed to ${action === "inc" ? "increment" : "decrement"} turn`);
          });
        }
        return res.json();
      })
      .then((updatedGame: Game) => {
        setCurrentGame(updatedGame);
        if (onUpdate) {
          onUpdate(updatedGame);
        }
      })
      .catch((err) => {
        setError(err.message || `Failed to ${action === "inc" ? "increment" : "decrement"} turn`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDecrementTurn = () => {
    setPendingTurnAction("dec");
    setShowTurnLengthModal(true);
  };

  const handleExtendGame = (turnsToAdd: number) => {
    setIsLoading(true);
    setError("");
    
    authenticatedFetch(`/games/extend?game_id=${encodeURIComponent(currentGame.game_id)}&turns=${turnsToAdd}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to extend game");
          });
        }
        return res.json();
      })
      .then((updatedGame: Game) => {
        setCurrentGame(updatedGame);
        if (onUpdate) {
          onUpdate(updatedGame);
        }
        setShowExtendGameModal(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to extend game");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === currentGame.game_status) {
      setShowStatusDropdown(false);
      return;
    }

    setIsLoading(true);
    setError("");

    authenticatedFetch(`/games/status?game_id=${currentGame.game_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        game_status: newStatus
      })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to update game status");
          });
        }
        return res.json();
      })
      .then((updatedGame: Game) => {
        setCurrentGame(updatedGame);
        setShowStatusDropdown(false);
        if (onUpdate) {
          onUpdate(updatedGame);
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to update game status");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleToggleTimerEnabled = () => {
    const newValue = !currentGame.timer_enabled;
    setIsLoading(true);
    setError("");

    authenticatedFetch(`/games/settings?game_id=${currentGame.game_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        timer_enabled: newValue
      })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to update timer setting");
          });
        }
        return res.json();
      })
      .then((updatedGame: Game) => {
        setCurrentGame(updatedGame);
        if (onUpdate) {
          onUpdate(updatedGame);
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to update timer setting");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleToggleLimitActions = () => {
    const newValue = !currentGame.limit_actions;
    setIsLoading(true);
    setError("");

    authenticatedFetch(`/games/settings?game_id=${currentGame.game_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        limit_actions: newValue
      })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to update action limit setting");
          });
        }
        return res.json();
      })
      .then((updatedGame: Game) => {
        setCurrentGame(updatedGame);
        if (onUpdate) {
          onUpdate(updatedGame);
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to update action limit setting");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div style={{
      padding: "1rem",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px solid #dee2e6",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      marginBottom: "1rem"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", gap: "1rem", flexWrap: "wrap" }}>
        {/* Group 1: Change Status Button */}
        <div style={{ position: "relative" }} ref={dropdownRef}>
          <button
            onClick={() => {
              setShowStatusDropdown(!showStatusDropdown);
            }}
            disabled={isLoading}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              backgroundColor: currentGame.game_status === "ACTIVE" ? "#d4edda" : 
                             currentGame.game_status === "COMPLETED" ? "#d1ecf1" : "#fff3cd",
              color: currentGame.game_status === "ACTIVE" ? "#155724" : 
                    currentGame.game_status === "COMPLETED" ? "#0c5460" : "#856404",
              border: "none",
              borderRadius: "4px",
              opacity: isLoading ? 0.6 : 1,
              fontWeight: "bold",
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {currentGame.game_name} Game Status: {currentGame.game_status}
          </button>
          {showStatusDropdown && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: "0.25rem",
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              minWidth: "150px"
            }}>
              {GAME_STATUSES.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={isLoading || status === currentGame.game_status}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "0.5rem 1rem",
                    textAlign: "left",
                    fontSize: "0.875rem",
                    cursor: (isLoading || status === currentGame.game_status) ? "not-allowed" : "pointer",
                    backgroundColor: status === currentGame.game_status ? "#e9ecef" : "white",
                    color: status === currentGame.game_status ? "#6c757d" : "#212529",
                    border: "none",
                    borderBottom: "1px solid #dee2e6"
                  }}
                >
                  {status}
                  {status === currentGame.game_status && " ✓"}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Group 4: Turn Counter, Inc Turn, Dec Turn */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#e9ecef",
            color: "#212529",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
            height: "2.5rem",
            textAlign: "center",
            boxSizing: "border-box",
            margin: 0
          }}>
            Turn: {currentGame.current_turn} / {currentGame.max_turns}
          </span>
          <button
            onClick={handleIncrementTurn}
            disabled={isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn >= currentGame.max_turns}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: (isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn >= currentGame.max_turns) ? "not-allowed" : "pointer",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: (isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn >= currentGame.max_turns) ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Inc Turn
          </button>
          <button
            onClick={handleDecrementTurn}
            disabled={isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn <= 1}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: (isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn <= 1) ? "not-allowed" : "pointer",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: (isLoading || currentGame.game_status !== "ACTIVE" || currentGame.current_turn <= 1) ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Dec Turn
          </button>
          <button
            onClick={() => setShowExtendGameModal(true)}
            disabled={isLoading || currentGame.game_status !== "ACTIVE"}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: (isLoading || currentGame.game_status !== "ACTIVE") ? "not-allowed" : "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: (isLoading || currentGame.game_status !== "ACTIVE") ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Extend Game
          </button>
        </div>
        
        {/* Group 4: Create Team, Copy Game ID */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => {
              closeAllSections();
              setShowCreateTeamForm(true);
            }}
            disabled={isLoading || currentGame.game_status !== "PRE-GAME"}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: (isLoading || currentGame.game_status !== "PRE-GAME") ? "not-allowed" : "pointer",
              backgroundColor: "#17a2b8",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: (isLoading || currentGame.game_status !== "PRE-GAME") ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Create Team
          </button>
          <button
            onClick={async (e) => {
              try {
                await navigator.clipboard.writeText(currentGame.game_id);
                // Show a brief success message
                const button = e.currentTarget;
                const originalText = button.textContent;
                button.textContent = "Copied!";
                setTimeout(() => {
                  button.textContent = originalText;
                }, 2000);
              } catch (err) {
                console.error("Failed to copy game ID:", err);
                alert(`Game ID: ${currentGame.game_id}`);
              }
            }}
            style={{
              padding: "0.5rem 1rem",
              cursor: "pointer",
              backgroundColor: "rgba(23, 162, 184, 0.1)",
              color: "#17a2b8",
              border: "1px solid rgba(23, 162, 184, 0.3)",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "bold",
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Copy Game ID to clipboard"
          >
            Copy Game ID
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={() => {
              closeAllSections();
              setShowViewTeams(true);
            }}
            disabled={isLoading}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              backgroundColor: "#6f42c1",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: isLoading ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Teams
          </button>
          <button
            onClick={() => {
              closeAllSections();
              setShowViewMessages(true);
            }}
            disabled={isLoading}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              backgroundColor: "#20c997",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: isLoading ? 0.6 : 1,
              height: "2.5rem",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            Messages
          </button>
          <div style={{ position: "relative", display: "inline-block" }}>
            <button
              onClick={() => {
                const willShowActions = !showViewActions;
                if (willShowActions) {
                  closeAllSections();
                  setShowViewActions(true);
                  // Clear notification when opening actions view
                  if (onClearActionsNotification) {
                    onClearActionsNotification();
                  }
                } else {
                  setShowViewActions(false);
                }
              }}
              disabled={isLoading}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                cursor: isLoading ? "not-allowed" : "pointer",
                backgroundColor: "#fd7e14",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: isLoading ? 0.6 : 1,
                height: "2.5rem",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              Actions
            </button>
          {hasUnreadActions && !showViewActions && (
            <span
              style={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                backgroundColor: "#dc3545",
                color: "white",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
              }}
              title="New actions available"
            >
              !
            </span>
          )}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.5rem", 
            cursor: isLoading ? "not-allowed" : "pointer",
            userSelect: "none",
            margin: 0,
            padding: "0.5rem 1rem",
            backgroundColor: "#e9ecef",
            borderRadius: "4px",
            height: "2.5rem",
            boxSizing: "border-box"
          }}>
            <input
              type="checkbox"
              checked={currentGame.timer_enabled ?? true}
              onChange={handleToggleTimerEnabled}
              disabled={isLoading}
              style={{ 
                width: "18px", 
                height: "18px", 
                cursor: isLoading ? "not-allowed" : "pointer" 
              }}
            />
            <span style={{ fontSize: "0.875rem", fontWeight: "500" }}>
              Timer Enabled
            </span>
          </label>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.5rem", 
            cursor: isLoading ? "not-allowed" : "pointer",
            userSelect: "none",
            margin: 0,
            padding: "0.5rem 1rem",
            backgroundColor: "#e9ecef",
            borderRadius: "4px",
            height: "2.5rem",
            boxSizing: "border-box"
          }}>
            <input
              type="checkbox"
              checked={currentGame.limit_actions ?? true}
              onChange={handleToggleLimitActions}
              disabled={isLoading}
              style={{ 
                width: "18px", 
                height: "18px", 
                cursor: isLoading ? "not-allowed" : "pointer" 
              }}
            />
            <span style={{ fontSize: "0.875rem", fontWeight: "500" }}>
              Limit Actions
            </span>
          </label>
        </div>
        {currentGame.timer_enabled && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <TurnTimer initialSeconds={displayTimerSeconds} isPaused={isPaused} />
            {displayTimerSeconds > 0 && (
              <button
                onClick={async () => {
                  if (isPaused) {
                    resumeTimer();
                    try {
                      await authenticatedFetch(`/games/timer/resume?game_id=${currentGame.game_id}`, {
                        method: "POST",
                      });
                    } catch (err) {
                      console.error("Failed to broadcast timer resume:", err);
                    }
                  } else {
                    pauseTimer();
                    try {
                      await authenticatedFetch(`/games/timer/pause?game_id=${currentGame.game_id}`, {
                        method: "POST",
                      });
                    } catch (err) {
                      console.error("Failed to broadcast timer pause:", err);
                    }
                  }
                }}
                style={{
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                  backgroundColor: isPaused ? "#28a745" : "#ffc107",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  height: "2.5rem",
                  boxSizing: "border-box"
                }}
                title={isPaused ? "Resume Timer" : "Pause Timer"}
              >
                {isPaused ? "▶" : "⏸"}
              </button>
            )}
            <button
              onClick={() => setShowExtendTimeModal(true)}
              style={{
                padding: "0.5rem 1rem",
                cursor: "pointer",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontSize: "0.875rem",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                height: "2.5rem",
                boxSizing: "border-box"
              }}
              title="Extend Turn Time"
            >
              +
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ 
          color: "red", 
          fontSize: "0.875rem", 
          marginTop: "0.5rem",
          padding: "0.75rem",
          backgroundColor: "#f8d7da",
          border: "1px solid #f5c6cb",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem"
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            style={{
              background: "none",
              border: "none",
              color: "red",
              cursor: "pointer",
              fontSize: "1.2rem",
              fontWeight: "bold",
              padding: "0",
              minWidth: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Close"
          >
            ×
          </button>
        </div>
      )}

      {showCreateTeamForm && (
        <div style={{
          marginTop: "1rem",
          padding: "1rem",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #dee2e6"
        }}>
          <CreateTeamForm
            game={currentGame}
            onClose={() => setShowCreateTeamForm(false)}
            onSuccess={() => setShowCreateTeamForm(false)}
          />
        </div>
      )}

      {showViewTeams && (
        <ViewTeams
          game={currentGame}
          onClose={() => setShowViewTeams(false)}
          teamsData={teamsForCurrentGame}
          playersByTeam={playersByTeam}
          playerActionCounts={playerActionCounts}
          playerCharCounts={playerCharCounts}
          playerLandingSiteInvestments={playerLandingSiteInvestments}
        />
      )}

      {showViewMessages && (
        <div style={{
          marginTop: "1rem",
          padding: "1rem",
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #dee2e6"
        }}>
          <ViewGameMessages
            gameId={currentGame.game_id}
            onClose={() => setShowViewMessages(false)}
            messagesData={messages}
          />
        </div>
      )}

      {showViewActions && (
        <div style={{
          marginTop: "1rem",
          padding: "1rem",
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #dee2e6"
        }}>
          <ViewGameActions
            gameId={currentGame.game_id}
            onClose={() => setShowViewActions(false)}
            actionsData={actions}
          />
        </div>
      )}

      <TurnLengthModal
        isOpen={showTurnLengthModal}
        onClose={() => {
          setShowTurnLengthModal(false);
          setPendingTurnAction(null);
        }}
        onSubmit={handleTurnLengthSubmit}
        defaultMinutes={10}
      />
      <ExtendTimeModal
        isOpen={showExtendTimeModal}
        onClose={() => setShowExtendTimeModal(false)}
        onSubmit={async (additionalSeconds: number) => {
          // Calculate current_remaining the same way extendTimer does (from localStorage)
          // This ensures consistency, especially when timer has ended
          let currentRemaining = displayTimerSeconds;
          const savedTimer = localStorage.getItem("turnTimer");
          const savedPause = localStorage.getItem("turnTimerPaused");
          
          if (savedTimer) {
            try {
              const timerData = JSON.parse(savedTimer);
              if (timerData.server_end_time && typeof timerData.time_offset === "number") {
                const current_server_time = Date.now() - timerData.time_offset;
                currentRemaining = Math.max(0, Math.floor((timerData.server_end_time - current_server_time) / 1000));
              } else if (timerData.endTime) {
                currentRemaining = Math.max(0, Math.floor((timerData.endTime - Date.now()) / 1000));
              }
            } catch (e) {
              // Fallback to displayTimerSeconds if parsing fails
            }
          } else if (savedPause) {
            try {
              const pauseData = JSON.parse(savedPause);
              if (typeof pauseData.remaining === "number") {
                currentRemaining = pauseData.remaining;
              }
            } catch (e) {
              // Fallback to displayTimerSeconds if parsing fails
            }
          }
          
          // Don't call extendTimer locally - let the WebSocket event handle it for consistency
          // This ensures all clients (including admin) get the same synchronized update
          try {
            await authenticatedFetch(`/games/timer/extend?game_id=${currentGame.game_id}&additional_seconds=${additionalSeconds}&current_remaining=${currentRemaining}&is_paused=${isPaused}`, {
              method: "POST",
            });
          } catch (err) {
            console.error("Failed to broadcast timer extend:", err);
          }
          setShowExtendTimeModal(false);
        }}
      />
      <ExtendGameModal
        isOpen={showExtendGameModal}
        onClose={() => setShowExtendGameModal(false)}
        onSubmit={handleExtendGame}
        isSubmitting={isLoading}
      />
    </div>
  );
}

